import { Globals } from '../../modules/Globals';
import axios from 'axios';
import { IBranch, IBranchContactDto } from '../../interfaces/IBranch';
import { IHourRange } from '../../interfaces/IHourRange';
import { PaymentMethod } from '../../enums/PaymentMethod';
import { IBranchPaymentsData } from '../../components/pages/IBranchPaymentsData';
import ITicketSettings from '../../interfaces/ITicketSettings';
import { mergeObjects } from '../../utils';
import { IOrderEmailSettings } from '../../interfaces/IOrderEmailSettings';
import { IPaginated } from '../../interfaces/IPaginated';
import { IOrder } from '../../interfaces/IOrder';
import IEmail from '../../interfaces/IEmail';

const apiUrl = Globals.apiPath + '/branches';

export const defaultTicketSettings: ITicketSettings = {
  showLogo: false,
  showQR: true,
  logoPath: null,
  ticketSize: 1,
  comandaSize: 1,
  showAddress: null,
  showCovers: true,
  startText: null,
  endText: null,
};

export default class BranchesService {
  constructor() {}

  async getAll(restaurantId: number): Promise<IBranch[]> {
    let res = await axios.get<IBranch[]>(`${apiUrl}?r=${restaurantId}`);
    return res.data;
  }

  async getById(id: number): Promise<IBranch> {
    let res = await axios.get<IBranch>(`${apiUrl}/${id}`);
    return res.data;
  }

  async create(branch: IBranch): Promise<IBranch> {
    let res = await axios.post<IBranch>(apiUrl, branch);
    return res.data;
  }

  async update(branch: IBranch): Promise<IBranch> {
    let res = await axios.put<IBranch>(`${apiUrl}/${branch.id}`, branch);
    return res.data;
  }

  /** Narrow phone/WhatsApp write; `update()` rewrites every branch field. */
  static async updateContact(
    id: number,
    dto: IBranchContactDto
  ): Promise<IBranch> {
    const res = await axios.patch<IBranch>(`${apiUrl}/${id}/contact`, dto);
    return res.data;
  }

  async getTicketSettings(id: number): Promise<ITicketSettings> {
    let res = await axios.get<{ settings: ITicketSettings }>(
      `${apiUrl}/${id}/ticketSettings`
    );

    return mergeObjects(defaultTicketSettings, res.data.settings);
  }

  static async saveIndexes({
    ids,
    indexes,
  }: {
    ids: number[];
    indexes: number[];
  }): Promise<IBranch[]> {
    let res = await axios.put<IBranch[]>(`${apiUrl}/indexes`, {
      ids,
      indexes,
    });
    return res.data;
  }

  async setTicketSettings(
    id: number,
    ticketSettings: ITicketSettings | null
  ): Promise<ITicketSettings> {
    const settings = { ...ticketSettings };

    for (const p in settings) {
      if (settings[p] === defaultTicketSettings[p]) delete settings[p];
    }

    let res = await axios.put<{ settings: ITicketSettings }>(
      `${apiUrl}/${id}/ticketSettings`,
      settings
    );

    return mergeObjects(defaultTicketSettings, res.data.settings);
  }

  async getMailSettings(id: number): Promise<IOrderEmailSettings> {
    let res = await axios.get<{ settings: IOrderEmailSettings }>(
      `${apiUrl}/${id}/mailSettings`
    );

    return res.data.settings;
  }

  async setMailSettings(
    id: number,
    mailSettings: IOrderEmailSettings | null
  ): Promise<IOrderEmailSettings> {
    let res = await axios.put<{ settings: IOrderEmailSettings }>(
      `${apiUrl}/${id}/mailSettings`,
      mailSettings
    );

    return res.data.settings;
  }

  async testMail(id: number, to: string) {
    return await axios.post(`${apiUrl}/${id}/testMail`, { to });
  }

  async getEmails(
    branchId: number,
    options = null
  ): Promise<IPaginated<IEmail>> {
    let path = `${apiUrl}/${branchId}/mails`;

    if (options) path += `?options=${JSON.stringify(options)}`;

    let res = await axios.get<IPaginated<IEmail>>(path);
    return res.data;
  }

  async getPaymentsData(id: number) {
    let res = await axios.get<IBranchPaymentsData>(
      `${apiUrl}/${id}/paymentsData`
    );
    return res.data;
  }

  async linkPaymentMethod(
    branch: IBranch,
    paymentMethod: PaymentMethod,
    data: any
  ): Promise<IBranch> {
    let res = await axios.put<IBranch>(`${apiUrl}/${branch.id}/paymentMethod`, {
      paymentMethod,
      data,
    });
    return res.data;
  }

  async unlinkPaymentMethod(
    branch: IBranch,
    paymentMethod: PaymentMethod
  ): Promise<IBranch> {
    let res = await axios.put<IBranch>(
      `${apiUrl}/${branch.id}/unlinkPaymentMethod`,
      { paymentMethod }
    );
    return res.data;
  }

  static async delete(id: number): Promise<IBranch> {
    let res = await axios.delete<IBranch>(`${apiUrl}/${id}`);
    return res.data;
  }

  async activate(branch: IBranch): Promise<IBranch> {
    let res = await axios.post<IBranch>(`${apiUrl}/${branch.id}/activate`);
    return res.data;
  }

  async deactivate(branch: IBranch): Promise<IBranch> {
    let res = await axios.post<IBranch>(`${apiUrl}/${branch.id}/deactivate`);
    return res.data;
  }

  async getHours(id: number): Promise<IHourRange[]> {
    let res = await axios.get<IHourRange[]>(`${apiUrl}/${id}/getHours`);
    return res.data;
  }

  async saveHours(id: number, hours: IHourRange[]): Promise<IHourRange[]> {
    let res = await axios.put<IHourRange[]>(`${apiUrl}/${id}/saveHours`, {
      hours,
    });
    return res.data;
  }
}
